import { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.rag-health.demo-connect.us';

interface User {
  id: string;
  email: string;
  name?: string;
  picture?: string;
  subscription_tier: string;
  roles: string[];
}

interface SessionState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  googleConnected: boolean;
}

interface UseSessionReturn extends SessionState {
  login: () => Promise<void>;
  logout: () => Promise<void>;
  checkSession: () => Promise<void>;
  connectGoogle: () => Promise<void>;
}

/**
 * Custom hook for BFF session-based authentication.
 *
 * Replaces Auth0 React SDK's useAuth0 hook.
 * Uses HTTP-only cookies for session management.
 */
export function useSession(): UseSessionReturn {
  const [state, setState] = useState<SessionState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
    googleConnected: false,
  });

  /**
   * Check if user has an active session.
   * Called on mount and after OAuth callback.
   */
  const checkSession = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/auth/me`, {
        method: 'GET',
        credentials: 'include', // Send cookies
      });

      if (res.ok) {
        const data = await res.json();
        if (data.authenticated && data.user) {
          setState({
            user: data.user,
            isLoading: false,
            isAuthenticated: true,
            googleConnected: data.googleConnected || false,
          });
          return;
        }
      }

      // Not authenticated
      setState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
        googleConnected: false,
      });
    } catch (error) {
      console.error('Session check failed:', error);
      setState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
        googleConnected: false,
      });
    }
  }, []);

  /**
   * Initiate login flow.
   * Calls /auth/login to get Auth0 authorization URL, then redirects.
   */
  const login = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!res.ok) {
        throw new Error('Failed to initiate login');
      }

      const data = await res.json();
      if (data.authorization_url) {
        // Redirect to Auth0
        window.location.href = data.authorization_url;
      } else {
        console.error('No authorization URL in response');
      }
    } catch (error) {
      console.error('Login failed:', error);
    }
  }, []);

  /**
   * Log out the user.
   * Calls /auth/logout to clear server-side session.
   */
  const logout = useCallback(async () => {
    try {
      await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout request failed:', error);
    }

    // Clear local state
    setState({
      user: null,
      isLoading: false,
      isAuthenticated: false,
      googleConnected: false,
    });

    // Redirect to Auth0 logout to clear Auth0 session
    const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN || 'violet-hookworm-18506.cic-demo-platform.auth0app.com';
    const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID || 'gYVmHq3MbrI73Uf1Qikb1ze5KfBdDLxe';
    const logoutUrl = `https://${auth0Domain}/v2/logout?client_id=${clientId}&returnTo=${encodeURIComponent(window.location.origin)}`;
    window.location.href = logoutUrl;
  }, []);

  /**
   * Initiate Google Connected Accounts flow.
   * Calls /auth/connect/google to get authorization URL, then redirects.
   */
  const connectGoogle = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/auth/connect/google`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!res.ok) {
        throw new Error('Failed to initiate Google connection');
      }

      const data = await res.json();
      if (data.authorization_url) {
        // Redirect to Auth0 for Google connection
        window.location.href = data.authorization_url;
      } else {
        console.error('No authorization URL in response');
      }
    } catch (error) {
      console.error('Connect Google failed:', error);
    }
  }, []);

  // Check session on mount
  useEffect(() => {
    checkSession();
  }, [checkSession]);

  // Handle OAuth callback (check URL for auth_error, code, or connected)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Check for auth error
    const authError = params.get('auth_error');
    if (authError) {
      console.error('Authentication error:', authError);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    // Check for successful Google connection
    const connected = params.get('connected');
    if (connected === 'google') {
      console.log('Google Calendar connected successfully');
      setState(prev => ({ ...prev, googleConnected: true }));
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    // If we have code/state params, the callback was handled server-side
    // and we just need to refresh session state
    if (params.has('code') || params.has('state')) {
      // Clean URL and refresh session
      window.history.replaceState({}, '', window.location.pathname);
      checkSession();
    }
  }, [checkSession]);

  return {
    user: state.user,
    isLoading: state.isLoading,
    isAuthenticated: state.isAuthenticated,
    googleConnected: state.googleConnected,
    login,
    logout,
    checkSession,
    connectGoogle,
  };
}

export default useSession;
